/**
 * useReservationMetadataMutation — senyals de la card (`plans/calendar-card-signals.md` §6 B1.4 i F2.2).
 *
 * `PATCH /reservations/:id/metadata` des del popover del calendari:
 *   - «Link enviat» / «Transferència demanada» (`paymentRequestedAt` + `paymentRequestedVia`) i
 *     «Treure» (`paymentRequestedAt: null`);
 *   - «Paga en arribar (aquesta reserva)»: `payOnArrival` sí, no o `null` («com la fitxa»);
 *   - «Qui paga»: `clientId` (una empresa) o `null` (l'hoste). El backend ja el porta a Beds24.
 *
 * Un camp absent no es toca; `null` l'esborra.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { PaymentRequestedVia } from '@guilleumes/types';
import { api } from '@/services/api';
import {
  SIMULATED_UPDATE_TOAST,
  isBeds24Simulated,
  showResultToast,
} from '@/lib/beds24-simulated-toast';

export interface ReservationMetadataPatch {
  payOnArrival?: boolean | null;
  /** ISO, o `null` per treure-la. */
  paymentRequestedAt?: string | null;
  paymentRequestedVia?: PaymentRequestedVia | null;
  clientId?: string | null;
}

export interface ReservationMetadataVariables {
  reservationId: string;
  patch: ReservationMetadataPatch;
}

/** La crida en si, fora del hook perquè es pugui provar amb `api` mockejat. */
export async function patchReservationMetadata({ reservationId, patch }: ReservationMetadataVariables) {
  const response = await api.patch(
    `/reservations/${encodeURIComponent(reservationId)}/metadata`,
    patch,
  );
  return response.data;
}

export function useReservationMetadataMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: patchReservationMetadata,
    onSuccess: (data, { reservationId }) => {
      // «Qui paga» escriu el client a Beds24 (custom1). Fora de producció no surt
      // (`beds24Simulated`): s'avisa; el camí de sempre segueix sense toast.
      if (isBeds24Simulated(data)) showResultToast(SIMULATED_UPDATE_TOAST);
      queryClient.invalidateQueries({ queryKey: ['calendar-bookings'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['reservation-full', reservationId] });
      // La fitxa (`ReservationSheet`) llegeix el `clientId` d'aquesta clau.
      queryClient.invalidateQueries({ queryKey: ['reservation-metadata', reservationId] });
    },
    onError: (error: unknown) => {
      const detail = error instanceof Error && error.message ? `: ${error.message}` : '';
      toast.error(`No s'ha pogut desar el pagament de la reserva${detail}`);
    },
  });
}

export default useReservationMetadataMutation;
